/**
 * Module → User Manual mapping.
 *
 * Single source of truth that links a paid add-on module to:
 *   - the section ids in `src/components/UserGuide.tsx` (in-app guide)
 *   - the anchors in `docs/CIMFONI_USER_GUIDE.md` (master doc)
 *
 * When an organization subscribes to a module, its guide sections become
 * visible in the in-app User Guide for that org. The master doc always
 * shows everything (tagged Optional) for sales / onboarding.
 *
 * Adding a new module — same PR as the module:
 *   1. Register the module key in `paid_modules` (DB).
 *   2. Add the in-app section in `UserGuide.tsx` with `moduleKey: '<key>'`.
 *   3. Add a section in `docs/CIMFONI_USER_GUIDE.md` under Optional Modules,
 *      tagged `_Module: <name> (optional)_`.
 *   4. Register both ids here.
 *   5. Append one line to `docs/Recent_Updates_Last_30_Days.md`.
 *
 * See `docs/CONTRIBUTING_MODULES.md` for the full checklist.
 */

import type { PaidModuleKey } from '@/hooks/useEntitlements';

export interface ModuleManualEntry {
  /** Human label for module (matches paid_modules.name). */
  label: string;
  /** Section ids in UserGuide.tsx that belong to this module. */
  guideSectionIds: string[];
  /** Anchor slugs in docs/CIMFONI_USER_GUIDE.md. */
  masterDocAnchors: string[];
}

export const MODULE_MANUAL_MAP: Record<PaidModuleKey, ModuleManualEntry> = {
  ai_assistant: {
    label: 'AI Assistant',
    guideSectionIds: [],
    masterDocAnchors: [],
  },
  advanced_analytics: {
    label: 'Advanced Analytics',
    guideSectionIds: [],
    masterDocAnchors: [],
  },
  inspections: {
    label: 'Inspections',
    guideSectionIds: [],
    masterDocAnchors: [],
  },
  training: {
    label: 'Training',
    guideSectionIds: [],
    masterDocAnchors: [],
  },
};

/** Returns true if the given guide section id is gated behind any module. */
export function getModuleForGuideSection(
  sectionId: string
): PaidModuleKey | null {
  for (const [key, entry] of Object.entries(MODULE_MANUAL_MAP)) {
    if (entry.guideSectionIds.includes(sectionId)) {
      return key as PaidModuleKey;
    }
  }
  return null;
}
